// apps/website/src/components/common/Layout/TopNav.tsx
//
// Restored from ui/revamp:src/components/common/Layout/TopNav.tsx, where the
// marketing header lived in the shared AppLayout. The monorepo split dropped
// that layout from the website, so the header is ported here.
//
// Two deliberate adaptations for the public marketing site:
//   1. No useAuth(). The old header swapped its actions for Dashboard/Leave
//      when signed in, but the website is a separate deployment with no app
//      session. It always shows the public CTAs.
//   2. The CTAs leave the website, so they are absolute anchors built from
//      webappUrl() rather than react-router <Link>. The logo stays internal.
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { webappUrl } from "../../../config/webapp";
import "../../../styles/components/topnav.css";

export const TopNav: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`top-nav-container ${isScrolled ? "top-nav-is-scrolled" : ""}`}
    >
      <div className="top-nav-inner">
        {/* Brand Section — stays on the marketing site */}
        <Link to="/" className="top-nav-logo-group">
          <div className="top-nav-logo-text">
            <span className="top-nav-brand-name">ORRA</span>
            <span className="top-nav-brand-status">KINGDOM</span>
          </div>
        </Link>

        {/* Desktop Actions */}
        <div className="top-nav-actions top-nav-desktop-only">
          <a href={webappUrl("/login")} className="top-nav-link-btn">
            LOG IN
          </a>
          <a href={webappUrl("/register")} className="top-nav-cta-pill">
            SIGN UP
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className="top-nav-mobile-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="top-nav-mobile-drawer"
          >
            <div className="top-nav-mobile-drawer-inner">
              <a
                href={webappUrl("/login")}
                className="top-nav-mobile-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                LOG IN
              </a>
              <a
                href={webappUrl("/register")}
                className="top-nav-cta-pill top-nav-mobile-cta"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                SIGN UP{" "}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
